/**
 * Possession-change trace — the §14 "possession diagnostics" side-channel.
 *
 * Every time `ball.possessedBy` changes, updatePossession() classifies WHY and
 * (when a sink is attached) records it here: previous possessor, new possessor,
 * a reason code, the challenger's approach angle relative to the incumbent, the
 * ball speed before/after, and the tick. This is what makes "the ball was stolen
 * off my back" a checkable claim instead of an anecdote.
 *
 * Like ball-trace.ts this is a DIAGNOSTIC side-channel: it is not part of
 * WorldState, is never hashed/serialized/rolled back, only READS state, and never
 * feeds back into the sim — so determinism is preserved and it is zero-cost (a
 * single null check) when no sink is attached.
 */

/**
 * Why possession changed. These are the directive's §14 "allowed reasons" — a
 * change that cannot be attributed to one of these is a bug, not a category.
 */
export const enum PossessionChangeReason {
  /** A slide tackle's leg reached the ball. */
  ValidSlideContact = 0,
  /** A standing challenge / frontal cut-off reached the ball from the front. */
  FrontalInterception = 1,
  /** A defender stepped into a travelling (kicked) ball. */
  PassInterception = 2,
  /** A loose ball (nobody was controlling it) was brought under control. */
  LooseBallControl = 3,
  /** A goalkeeper collected the ball. */
  GoalkeeperCollection = 4,
  /** A restart assigned the ball to a taker. */
  RestartAssignment = 5,
  /** A committed standing tackle (in the StandingTackle anim) won the ball —
   *  distinct from a pure frontal cut-off with no tackle. Appended (=6) so the
   *  existing enum values stay stable for serialized traces and tests. */
  ValidStandingTackle = 6,
}

// Index MUST equal the enum value (POSSESSION_REASON_NAMES[reason]); the two are
// kept in lockstep by construction — do not reorder without renumbering the enum.
export const POSSESSION_REASON_NAMES: readonly string[] = [
  'valid_slide_contact',
  'frontal_interception',
  'pass_interception',
  'loose_ball_control',
  'goalkeeper_collection',
  'restart_assignment',
  'valid_standing_tackle',
];

/**
 * The current possession phase of the ball — the "clear possession states" the
 * §14 / Phase-1 directive asks for. Purely DERIVED from WorldState every tick by
 * `classifyPossessionState` (ball.ts); never stored, never hashed, so it costs
 * no determinism surface. Used by the gameplay debug overlay and readable by
 * tests as a stable classification of "who, if anyone, owns the ball right now".
 */
export const enum PossessionState {
  /** A field player has the ball under control. */
  Controlled = 0,
  /** No possessor; the ball is on the ground, slow — a settled loose ball. */
  Loose = 1,
  /** No possessor; the ball is airborne or travelling fast (a kick/pass/shot). */
  InFlight = 2,
  /** Controlled, but an opponent is challenging within tackle range right now. */
  Contested = 3,
  /** A goalkeeper is holding the ball. */
  GoalkeeperControlled = 4,
  /** The ball is out of live play (a restart/stoppage is in progress). */
  OutOfPlay = 5,
}

export const POSSESSION_STATE_NAMES: readonly string[] = [
  'CONTROLLED',
  'LOOSE',
  'IN_FLIGHT',
  'CONTESTED',
  'GOALKEEPER_CONTROLLED',
  'OUT_OF_PLAY',
];

/** One possession change, stamped with the sim tick. */
export interface PossessionChangeRecord {
  tick: number;
  /** Previous possessor PlayerId, or -1 if the ball was loose. */
  prev: number;
  /** New possessor PlayerId. */
  next: number;
  reason: PossessionChangeReason;
  /** dot(incumbent facing, incumbent→challenger): +1 head-on front, -1 rear.
   *  0 when there was no incumbent (a loose ball). */
  approachDot: number;
  /** Ball planar speed just before / after the change (m/s). */
  ballSpeedBefore: number;
  ballSpeedAfter: number;
}

/** A rejected steal: an opponent was closest but the challenge was not valid
 *  (a rear/side body brush on a shielding carrier), so possession did NOT change. */
export interface RejectedStealRecord {
  tick: number;
  incumbent: number;
  challenger: number;
  approachDot: number;
}

export class PossessionTrace {
  readonly changes: PossessionChangeRecord[] = [];
  readonly rejectedSteals: RejectedStealRecord[] = [];
  enabled = true;
  /**
   * When false, record*() updates `lastChange`/`lastRejected` (O(1), no growth)
   * but does NOT push to the history arrays. A live client overlay that only
   * needs the latest event installs the sink with `history = false` so a
   * long-running match cannot grow the arrays without bound; tests keep the
   * default (true) and read the full `changes`/`rejectedSteals` history.
   */
  history = true;
  /** The most recent recorded change / rejected steal, for a live readout. */
  lastChange: PossessionChangeRecord | null = null;
  lastRejected: RejectedStealRecord | null = null;

  recordChange(r: PossessionChangeRecord): void {
    if (!this.enabled) return;
    this.lastChange = r;
    if (this.history) this.changes.push(r);
  }

  recordRejectedSteal(r: RejectedStealRecord): void {
    if (!this.enabled) return;
    this.lastRejected = r;
    if (this.history) this.rejectedSteals.push(r);
  }

  clear(): void {
    this.changes.length = 0;
    this.rejectedSteals.length = 0;
    this.lastChange = null;
    this.lastRejected = null;
  }

  /** Count of changes per reason, indexed by PossessionChangeReason. */
  countsByReason(): number[] {
    const counts = new Array(POSSESSION_REASON_NAMES.length).fill(0);
    for (const c of this.changes) counts[c.reason]++;
    return counts;
  }
}

// ── Module-level active sink (diagnostic only; see header) ───────────────────
let active: PossessionTrace | null = null;

export function setPossessionTrace(sink: PossessionTrace | null): void {
  active = sink;
}

export function getPossessionTrace(): PossessionTrace | null {
  return active;
}

export function possessionTraceOn(): boolean {
  return active !== null && active.enabled;
}
